import { useState } from "react";
import {
  type PackageRow,
  type SkillState,
  usePatchPackage,
  useSetPackageState,
} from "../hooks/usePackages.js";

/**
 * 管控配置 Modal:
 *   - managed toggle: 是否纳入 mhwork 平台远程管控
 *   - state radio (仅 managed=1 时可改): enabled/disabled/removed/deprecated
 *   - reason: 切 state 时必填一句原因(进 audit_log)
 *
 * 后端约束:state 只能在 managed=1 的包上切。Modal 里如果用户先关 managed 再切
 * state, 会先发 PATCH (managed) 再发 POST (state), 第二次后端会 409 — 我们前端
 * 提前 disable 掉 state radio 避免这种状况。
 */
export function ManageSkillModal({
  pkg,
  onClose,
}: {
  pkg: PackageRow;
  onClose: () => void;
}) {
  const patchPkg = usePatchPackage();
  const setState = useSetPackageState();

  const [managed, setManaged] = useState<boolean>(pkg.managed === 1);
  const [state, setState_] = useState<SkillState>(
    (pkg.latest_state as SkillState | null) ?? "enabled",
  );
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const initialManaged = pkg.managed === 1;
  const initialState = (pkg.latest_state as SkillState | null) ?? "enabled";
  const managedChanged = managed !== initialManaged;
  const stateChanged = managed && state !== initialState;
  const dirty = managedChanged || stateChanged;
  const needsReason = stateChanged && state !== "enabled"; // 仅"启用"不需理由

  const busy = patchPkg.isPending || setState.isPending;

  const handleSave = async () => {
    setErr(null);
    if (needsReason && !reason.trim()) {
      setErr("停用 / 下架 / 弃用 必须填写原因(进审计日志)");
      return;
    }
    try {
      // managed 变化先发(后端约束:state 只能改 managed=1 的包)
      if (managedChanged) {
        await patchPkg.mutateAsync({ name: pkg.name, managed });
      }
      // state 只在 managed 是开的情况下生效
      if (managed && stateChanged) {
        await setState.mutateAsync({
          name: pkg.name,
          state,
          reason: reason.trim() || undefined,
        });
      }
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="modal-mask" onClick={busy ? undefined : onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          width: 460,
          padding: 24,
        }}
      >
        <div>
          <h2 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>
            管控配置
          </h2>
          <p className="page-sub" style={{ marginTop: 4 }}>
            <code className="code-soft">{pkg.name}</code>
          </p>
        </div>

        {/* managed toggle */}
        <label
          style={{
            display: "flex",
            gap: 12,
            padding: 12,
            border: "1px solid var(--border)",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={managed}
            onChange={(e) => setManaged(e.target.checked)}
            disabled={busy}
            style={{ marginTop: 2 }}
          />
          <div>
            <div style={{ fontWeight: 500, fontSize: 14 }}>纳入 mhwork 平台远程管控</div>
            <div className="page-sub" style={{ marginTop: 4 }}>
              开启后 mhwork 客户端会拉到这个 skill 的策略,并在事故时执行
              停用 / 下架。关闭则跟普通广场 skill 一样,平台不替作者扛事故。
            </div>
          </div>
        </label>

        {/* state radio */}
        <fieldset
          style={{
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: 12,
            opacity: managed ? 1 : 0.5,
          }}
          disabled={!managed || busy}
        >
          <legend style={{ padding: "0 6px", fontSize: 12, color: "var(--text-muted)" }}>
            当前状态(仅受管时生效)
          </legend>
          {(
            [
              ["enabled", "启用", "客户端正常显示和使用"],
              ["disabled", "停用", "客户端隐藏 + OpenClaw 看不到 — 事故首选"],
              ["removed", "下架", "永久下架 — 客户端可视情况删本地副本"],
              ["deprecated", "弃用", "仍可用,客户端给迁移提示"],
            ] as Array<[SkillState, string, string]>
          ).map(([value, label, desc]) => (
            <label
              key={value}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                padding: "6px 0",
                cursor: managed ? "pointer" : "not-allowed",
              }}
            >
              <input
                type="radio"
                name="state"
                value={value}
                checked={state === value}
                onChange={() => setState_(value)}
                style={{ marginTop: 3 }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: state === value ? 500 : 400 }}>
                  {label}
                </div>
                <div className="page-sub" style={{ fontSize: 11, marginTop: 2 }}>
                  {desc}
                </div>
              </div>
            </label>
          ))}
        </fieldset>

        {/* reason */}
        {needsReason && (
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
              原因(必填,进审计日志):
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="例: 0.x 版本 SQL 注入,等修复"
              disabled={busy}
            />
          </label>
        )}

        {err && (
          <div className="err" style={{ fontSize: 12 }}>
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={onClose}
            disabled={busy}
          >
            取消
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 2 }}
            onClick={handleSave}
            disabled={!dirty || busy}
          >
            {busy ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}
